import {
  type ACCOUNTING_EXPORT_ADAPTERS,
  type ACCOUNTING_EXPORT_FORMATS,
  accountingExportRequestSchema,
  type OpeningImportPreview,
} from "@station/contracts";
import {
  Alert,
  Button,
  Card,
  DateInput,
  FormField,
  MoneyText,
  Select,
  SkeletonRows,
  StatusBadge,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  useToast,
} from "@station/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { Download, Upload } from "lucide-react";
import { type ReactElement, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { AccountingPage, companySearchShape } from "../../../features/accounting/company.js";
import {
  base64ToBlob,
  downloadBlob,
  startOfYear,
  todayIstanbul,
} from "../../../features/accounting/shared.js";
import { zodErrorMap } from "../../../features/accounting/validation.js";
import { uploadFile } from "../../../features/station-import.js";
import { api, API_URL } from "../../../lib/api.js";
import { authStore } from "../../../lib/auth-store.js";
import { errorMessage } from "../../../lib/errors.js";
import { queryKeys } from "../../../lib/query.js";
import { usePermissions } from "../../../lib/session.js";

const searchSchema = z.object({
  ...companySearchShape,
  tab: z.enum(["opening", "export"]).optional().catch(undefined),
});

export const Route = createFileRoute("/_app/muhasebe/araclar")({
  validateSearch: (search) => searchSchema.parse(search),
  component: ToolsPage,
});

function ToolsPage() {
  const { t } = useTranslation();
  const search = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const permissions = usePermissions();
  const canImport = permissions.has("accounting.entry.manage");
  const canExport = permissions.has("accounting.export");
  const tab = search.tab ?? (canImport ? "opening" : "export");
  return (
    <AccountingPage title={t("menu.accountingTools")} description={t("accounting.tools.description")}>
      {({ companyId }) => (
        <Tabs
          value={tab}
          onValueChange={(next) =>
            void navigate({
              search: (previous) => ({ company: previous.company, tab: next as "opening" | "export" }),
              replace: true,
            })
          }
        >
          <TabsList aria-label={t("menu.accountingTools")}>
            {canImport ? (
              <TabsTrigger value="opening">{t("accounting.tools.tabs.opening")}</TabsTrigger>
            ) : null}
            {canExport ? <TabsTrigger value="export">{t("accounting.tools.tabs.export")}</TabsTrigger> : null}
          </TabsList>
          {canImport ? (
            <TabsContent value="opening">
              <OpeningImport companyId={companyId} />
            </TabsContent>
          ) : null}
          {canExport ? (
            <TabsContent value="export">
              <LedgerExport companyId={companyId} />
            </TabsContent>
          ) : null}
        </Tabs>
      )}
    </AccountingPage>
  );
}

async function downloadOpeningTemplate(): Promise<void> {
  const response = await fetch(`${API_URL}/accounting/opening-balances/template`, {
    headers: { authorization: `Bearer ${authStore.getAccessToken() ?? ""}` },
  });
  if (!response.ok) throw new Error(String(response.status));
  downloadBlob(await response.blob(), "acilis-bakiyeleri-sablonu.csv");
}

/** Açılış fişi içe aktarma: şablon indir → dosya yükle → ÖNİZLEME (satır/dosya hataları, denge) → taslak fiş. */
function OpeningImport({ companyId }: { companyId: string }): ReactElement {
  const { t } = useTranslation();
  const toast = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const inputRef = useRef<HTMLInputElement>(null);
  const [postingDate, setPostingDate] = useState<string | null>(startOfYear(todayIstanbul()));
  const [fileId, setFileId] = useState<string | null>(null);
  const [preview, setPreview] = useState<OpeningImportPreview | null>(null);
  const [busy, setBusy] = useState<"upload" | "commit" | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function handleFile(file: File): Promise<void> {
    if (!postingDate) return setError(t("accounting.opening.dateRequired"));
    setError(null);
    setPreview(null);
    setBusy("upload");
    try {
      const uploaded = await uploadFile(file, "IMPORT");
      setFileId(uploaded);
      setPreview(
        (
          await api.POST("/accounting/opening-balances/preview", {
            body: { companyId, fileId: uploaded, postingDate },
          })
        ).data!,
      );
    } catch (caught) {
      setError(errorMessage(caught));
    } finally {
      setBusy(null);
      if (inputRef.current) inputRef.current.value = "";
    }
  }

  async function commit(): Promise<void> {
    if (!fileId || !postingDate) return;
    setBusy("commit");
    setError(null);
    try {
      const entry = (
        await api.POST("/accounting/opening-balances/import", { body: { companyId, fileId, postingDate } })
      ).data!;
      await queryClient.invalidateQueries({ queryKey: queryKeys.accounting(companyId) });
      toast({ tone: "success", title: t("accounting.opening.created") });
      await navigate({ to: "/muhasebe/fisler/$id", params: { id: entry.id } });
    } catch (caught) {
      setError(errorMessage(caught));
    } finally {
      setBusy(null);
    }
  }

  return (
    <Card className="grid gap-4 p-5">
      <p className="text-sm text-muted-foreground">{t("accounting.opening.description")}</p>
      <div className="flex flex-wrap items-end gap-3">
        <FormField
          label={t("accounting.fields.postingDate")}
          description={t("accounting.opening.dateHint")}
          className="w-48"
          required
        >
          {(control) => (
            <DateInput
              {...control}
              value={postingDate}
              onValueChange={(next) => {
                setPostingDate(next);
                setPreview(null);
              }}
            />
          )}
        </FormField>
      </div>
      <div className="flex flex-wrap gap-2">
        <Button
          variant="outline"
          onClick={() =>
            void downloadOpeningTemplate().catch(() => setError(t("accounting.opening.templateFailed")))
          }
        >
          <Download aria-hidden="true" />
          {t("accounting.opening.template")}
        </Button>
        <Button onClick={() => inputRef.current?.click()} loading={busy === "upload"} disabled={!postingDate}>
          <Upload aria-hidden="true" />
          {t("accounting.opening.chooseFile")}
        </Button>
        <input
          ref={inputRef}
          type="file"
          accept=".csv,.xlsx,.xls"
          className="sr-only"
          tabIndex={-1}
          aria-label={t("accounting.opening.chooseFile")}
          onChange={(event) => event.target.files?.[0] && void handleFile(event.target.files[0])}
        />
      </div>
      {error ? <Alert tone="danger">{error}</Alert> : null}

      {preview ? (
        <div className="grid gap-3">
          <div className="flex flex-wrap items-center gap-x-5 gap-y-1 text-sm">
            <StatusBadge tone={preview.valid ? "success" : "danger"}>
              {preview.valid ? t("accounting.opening.valid") : t("accounting.opening.invalid")}
            </StatusBadge>
            <span>
              {t("accounting.entry.totalDebit")}:{" "}
              <MoneyText value={preview.totalDebit} className="font-semibold" />
            </span>
            <span>
              {t("accounting.entry.totalCredit")}:{" "}
              <MoneyText value={preview.totalCredit} className="font-semibold" />
            </span>
            <span>
              {t("accounting.entry.difference")}:{" "}
              <MoneyText value={preview.difference} colorize className="font-semibold" />
            </span>
          </div>
          {preview.errors.length > 0 ? (
            <Alert tone="danger" title={t("accounting.opening.fileErrors")}>
              <ul className="grid list-disc gap-0.5 pl-4">
                {preview.errors.map((message) => (
                  <li key={message}>{message}</li>
                ))}
              </ul>
            </Alert>
          ) : null}
          <div className="max-h-96 overflow-auto rounded-md border">
            <table className="w-full text-sm">
              <caption className="sr-only">{t("accounting.opening.previewCaption")}</caption>
              <thead className="sticky top-0 bg-muted text-xs uppercase text-muted-foreground">
                <tr>
                  <th scope="col" className="px-3 py-2 text-left">
                    {t("accounting.opening.row")}
                  </th>
                  <th scope="col" className="px-3 py-2 text-left">
                    {t("accounting.fields.account")}
                  </th>
                  <th scope="col" className="px-3 py-2 text-left">
                    {t("accounting.dimensions.STATION")}
                  </th>
                  <th scope="col" className="px-3 py-2 text-right">
                    {t("accounting.fields.debit")}
                  </th>
                  <th scope="col" className="px-3 py-2 text-right">
                    {t("accounting.fields.credit")}
                  </th>
                  <th scope="col" className="px-3 py-2 text-left">
                    {t("accounting.opening.result")}
                  </th>
                </tr>
              </thead>
              <tbody>
                {preview.rows.map((row) => (
                  <tr key={row.rowNo} className="border-t align-top">
                    <td className="px-3 py-2 tabular">{row.rowNo}</td>
                    <td className="px-3 py-2">
                      <span className="font-mono text-xs font-semibold">{row.accountCode}</span>{" "}
                      {row.accountName ?? ""}
                    </td>
                    <td className="px-3 py-2 font-mono text-xs">{row.stationCode ?? "—"}</td>
                    <td className="px-3 py-2 text-right">
                      <MoneyText value={row.debit} />
                    </td>
                    <td className="px-3 py-2 text-right">
                      <MoneyText value={row.credit} />
                    </td>
                    <td className="px-3 py-2">
                      {row.errors.length === 0 ? (
                        <StatusBadge tone="success">{t("accounting.opening.ok")}</StatusBadge>
                      ) : (
                        <ul className="grid gap-0.5 text-destructive">
                          {row.errors.map((message) => (
                            <li key={message}>{message}</li>
                          ))}
                        </ul>
                      )}
                      {row.notes.length > 0 ? (
                        <ul className="mt-1 grid gap-0.5 text-xs text-muted-foreground">
                          {row.notes.map((note) => (
                            <li key={note}>{note}</li>
                          ))}
                        </ul>
                      ) : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {!preview.valid ? <Alert tone="warning">{t("accounting.opening.fixAndRetry")}</Alert> : null}
          <div className="flex justify-end">
            <Button onClick={() => void commit()} disabled={!preview.valid} loading={busy === "commit"}>
              {t("accounting.opening.commit")}
            </Button>
          </div>
        </div>
      ) : null}
    </Card>
  );
}

type ExportAdapter = (typeof ACCOUNTING_EXPORT_ADAPTERS)[number];
type ExportFormat = (typeof ACCOUNTING_EXPORT_FORMATS)[number];

/** Dış muhasebe programına aktarım. Biçimi teyit edilmemiş adaptörler (iskelet) listede devre dışı görünür. */
function LedgerExport({ companyId }: { companyId: string }): ReactElement {
  const { t } = useTranslation();
  const toast = useToast();
  const today = todayIstanbul();
  const [adapter, setAdapter] = useState<ExportAdapter>("GENERIC");
  const [format, setFormat] = useState<ExportFormat>("XLSX");
  const [dateFrom, setDateFrom] = useState<string | null>(startOfYear(today));
  const [dateTo, setDateTo] = useState<string | null>(today);
  const [error, setError] = useState<string | null>(null);

  const adapters = useQuery({
    queryKey: queryKeys.accounting(companyId, "export-adapters"),
    staleTime: 3_600_000,
    queryFn: async () => (await api.GET("/accounting/export/adapters")).data!.items,
  });
  const selected = adapters.data?.find((item) => item.adapter === adapter);
  const formats = selected?.formats ?? [];

  const run = useMutation({
    mutationFn: async () => {
      const body = accountingExportRequestSchema.parse(
        { companyId, adapter, format, dateFrom, dateTo },
        { error: zodErrorMap },
      );
      return (await api.POST("/accounting/export", { body })).data!;
    },
    onMutate: () => setError(null),
    onSuccess: (result) => {
      downloadBlob(base64ToBlob(result.contentBase64, result.mimeType), result.fileName);
      toast({
        tone: "success",
        title: t("accounting.export.done", { entries: result.entryCount, lines: result.lineCount }),
      });
    },
    onError: (caught) =>
      setError(caught instanceof z.ZodError ? caught.issues[0]!.message : errorMessage(caught)),
  });

  if (adapters.error) return <Alert tone="danger">{errorMessage(adapters.error)}</Alert>;
  if (!adapters.data) return <SkeletonRows rows={4} />;

  return (
    <Card className="grid gap-4 p-5">
      <p className="text-sm text-muted-foreground">{t("accounting.export.description")}</p>
      <form
        className="grid gap-4"
        noValidate
        onSubmit={(event) => {
          event.preventDefault();
          if (!dateFrom || !dateTo) return setError(t("accounting.export.datesRequired"));
          run.mutate();
        }}
      >
        {error ? <Alert tone="danger">{error}</Alert> : null}
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <FormField label={t("accounting.export.adapter")} required>
            {(control) => (
              <Select
                {...control}
                value={adapter}
                onChange={(event) => {
                  const next = event.target.value as ExportAdapter;
                  setAdapter(next);
                  const nextFormats = adapters.data.find((item) => item.adapter === next)?.formats ?? [];
                  if (!nextFormats.includes(format) && nextFormats[0]) setFormat(nextFormats[0]);
                }}
              >
                {adapters.data.map((item) => (
                  <option key={item.adapter} value={item.adapter} disabled={!item.available}>
                    {item.name}
                    {item.available ? "" : ` — ${t("accounting.export.needsConfirmation")}`}
                  </option>
                ))}
              </Select>
            )}
          </FormField>
          <FormField label={t("accounting.export.format")} required>
            {(control) => (
              <Select
                {...control}
                value={format}
                onChange={(event) => setFormat(event.target.value as ExportFormat)}
              >
                {formats.map((item) => (
                  <option key={item} value={item}>
                    {item}
                  </option>
                ))}
              </Select>
            )}
          </FormField>
          <FormField label={t("accounting.fields.dateFrom")} required>
            {(control) => <DateInput {...control} value={dateFrom} onValueChange={setDateFrom} />}
          </FormField>
          <FormField label={t("accounting.fields.dateTo")} required>
            {(control) => <DateInput {...control} value={dateTo} onValueChange={setDateTo} />}
          </FormField>
        </div>
        {adapters.data.some((item) => !item.available) ? (
          <ul
            className="grid gap-1 text-sm text-muted-foreground"
            aria-label={t("accounting.export.pendingAdapters")}
          >
            {adapters.data
              .filter((item) => !item.available)
              .map((item) => (
                <li key={item.adapter}>
                  <span className="font-medium text-foreground">{item.name}:</span>{" "}
                  {item.note ?? t("accounting.export.needsConfirmation")}
                </li>
              ))}
          </ul>
        ) : null}
        <div className="flex justify-end">
          <Button type="submit" loading={run.isPending} disabled={!selected?.available}>
            <Download aria-hidden="true" />
            {t("accounting.export.run")}
          </Button>
        </div>
      </form>
      <Alert tone="info">{t("accounting.export.eLedgerNote")}</Alert>
    </Card>
  );
}
